import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en/translation.json';
import enSummary from '../locales/en/summary.json';
import enCpeCards from '../locales/en/cpeCards.json';
import enList from '../locales/en/list.json';
import enTableWidget from '../locales/en/tableWidget.json';
import enThreatsPage from '../locales/en/threatsPage.json';
import enDnsCategories from '../locales/en/dnsCategories.json';
import enActivity from '../locales/en/activity.json';
import enTools from '../locales/en/tools.json';
import enSettings from '../locales/en/settings.json';
import enCpeDetails from '../locales/en/cpeDetails.json';
import enCpeTabs from '../locales/en/cpeTabs.json';
import enDeviceDetails from '../locales/en/deviceDetails.json';
import enRiskScore from '../locales/en/riskScore.json';
import enMaster from '../locales/en/master.json';
import enWidgets from '../locales/en/widgets.json';
import rs from '../locales/rs/translation.json';
import rsSummary from '../locales/rs/summary.json';
import rsCpeCards from '../locales/rs/cpeCards.json';
import rsList from '../locales/rs/list.json';
import rsTableWidget from '../locales/rs/tableWidget.json';
import rsThreatsPage from '../locales/rs/threatsPage.json';
import rsActivity from '../locales/rs/activity.json';
import rsTools from '../locales/rs/tools.json';
import rsSettings from '../locales/rs/settings.json';
import rsCpeDetails from '../locales/rs/cpeDetails.json';
import rsCpeTabs from '../locales/rs/cpeTabs.json';
import rsDeviceDetails from '../locales/rs/deviceDetails.json';
import rsRiskScore from '../locales/rs/riskScore.json';
import rsMaster from '../locales/rs/master.json';
import rsWidgets from '../locales/rs/widgets.json';
import es from '../locales/es/translation.json';
import esSummary from '../locales/es/summary.json';
import esCpeCards from '../locales/es/cpeCards.json';
import esList from '../locales/es/list.json';
import esTableWidget from '../locales/es/tableWidget.json';
import esThreatsPage from '../locales/es/threatsPage.json';
import esActivity from '../locales/es/activity.json';
import esTools from '../locales/es/tools.json';
import esSettings from '../locales/es/settings.json';
import esCpeDetails from '../locales/es/cpeDetails.json';
import esCpeTabs from '../locales/es/cpeTabs.json';
import esDeviceDetails from '../locales/es/deviceDetails.json';
import esRiskScore from '../locales/es/riskScore.json';
import esMaster from '../locales/es/master.json';
import esWidgets from '../locales/es/widgets.json';
import sv from '../locales/sv/translation.json';
import svSummary from '../locales/sv/summary.json';
import svCpeCards from '../locales/sv/cpeCards.json';
import svList from '../locales/sv/list.json';
import svTableWidget from '../locales/sv/tableWidget.json';
import svThreatsPage from '../locales/sv/threatsPage.json';
import svActivity from '../locales/sv/activity.json';
import svTools from '../locales/sv/tools.json';
import svSettings from '../locales/sv/settings.json';
import svCpeDetails from '../locales/sv/cpeDetails.json';
import svCpeTabs from '../locales/sv/cpeTabs.json';
import svDeviceDetails from '../locales/sv/deviceDetails.json';
import svRiskScore from '../locales/sv/riskScore.json';
import svMaster from '../locales/sv/master.json';
import svWidgets from '../locales/sv/widgets.json';
import { DEFAULT_LANGUAGE, LANGUAGES, getLanguage } from './languages';

const STORAGE_KEY = 'language';

// Per-page strings live in their own files (src/locales/<lng>/<section>.json) and are
// mounted as top-level sections of the default namespace: t('summary.title'), etc.
const bundle = (base, sections) => ({ translation: { ...base, ...sections } });

const resources = {
  en: bundle(en, {
    summary: enSummary,
    cpeCards: enCpeCards,
    list: enList,
    tableWidget: enTableWidget,
    threatsPage: enThreatsPage,
    dnsCategories: enDnsCategories,
    activity: enActivity,
    tools: enTools,
    settings: enSettings,
    cpeDetails: enCpeDetails,
    cpeTabs: enCpeTabs,
    deviceDetails: enDeviceDetails,
    riskScore: enRiskScore,
    master: enMaster,
    widgets: enWidgets,
  }),
  // Serbian files live in locales/rs, but i18next needs 'sr' for Serbian plural forms (_few)
  sr: bundle(rs, {
    summary: rsSummary,
    cpeCards: rsCpeCards,
    list: rsList,
    tableWidget: rsTableWidget,
    threatsPage: rsThreatsPage,
    activity: rsActivity,
    tools: rsTools,
    settings: rsSettings,
    cpeDetails: rsCpeDetails,
    cpeTabs: rsCpeTabs,
    deviceDetails: rsDeviceDetails,
    riskScore: rsRiskScore,
    master: rsMaster,
    widgets: rsWidgets,
  }),
  es: bundle(es, {
    summary: esSummary,
    cpeCards: esCpeCards,
    list: esList,
    tableWidget: esTableWidget,
    threatsPage: esThreatsPage,
    activity: esActivity,
    tools: esTools,
    settings: esSettings,
    cpeDetails: esCpeDetails,
    cpeTabs: esCpeTabs,
    deviceDetails: esDeviceDetails,
    riskScore: esRiskScore,
    master: esMaster,
    widgets: esWidgets,
  }),
  sv: bundle(sv, {
    summary: svSummary,
    cpeCards: svCpeCards,
    list: svList,
    tableWidget: svTableWidget,
    threatsPage: svThreatsPage,
    activity: svActivity,
    tools: svTools,
    settings: svSettings,
    cpeDetails: svCpeDetails,
    cpeTabs: svCpeTabs,
    deviceDetails: svDeviceDetails,
    riskScore: svRiskScore,
    master: svMaster,
    widgets: svWidgets,
  }),
};

function readSavedLanguage() {
  try {
    // 'selectedLanguage' was written by an older picker in App.tsx
    let saved = localStorage.getItem(STORAGE_KEY) || localStorage.getItem('selectedLanguage');
    if (saved === 'rs') saved = 'sr'; // Serbian used to be stored under its country code
    if (LANGUAGES.some((l) => l.code === saved)) return saved;
  } catch {
    // storage blocked — use the default
  }
  return DEFAULT_LANGUAGE;
}

function applyDocumentLanguage(code) {
  document.documentElement.lang = getLanguage(code).locale;
}

i18n.use(initReactI18next).init({
  resources,
  lng: readSavedLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES.map((l) => l.code),
  interpolation: {
    escapeValue: false,
  },
});

applyDocumentLanguage(i18n.language);

i18n.on('languageChanged', (lng) => {
  applyDocumentLanguage(lng);
  try {
    localStorage.setItem(STORAGE_KEY, lng);
    localStorage.removeItem('selectedLanguage');
  } catch {
    // not persisted; still applied for this session
  }
});

export default i18n;
